import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../../config/config";

// All /api/rent/* routes are authenticated, but this file never sent a token
// - every call used to fail with 401. That's why the rent screens appeared
// empty.
const authHeaders = () => {
  const token =
    localStorage.getItem("token") || sessionStorage.getItem("ots_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Get pending rent for tenant
export const GetPendingRent = createAsyncThunk(
  "rentPayment/getPending",
  async ({ tenantId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/rent/pending/${tenantId}`, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch pending rent" });
    }
  }
);

// Get current rent due with breakdown
export const GetRentDue = createAsyncThunk(
  "rentPayment/getRentDue",
  async ({ tenancyId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/rent/due/${tenancyId}`, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch rent due" });
    }
  }
);

// Initiate rent payment
export const InitiateRentPayment = createAsyncThunk(
  "rentPayment/initiate",
  async ({ paymentData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/rent/pay`, paymentData, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to initiate payment" });
    }
  }
);

// Verify rent payment
export const VerifyRentPayment = createAsyncThunk(
  "rentPayment/verify",
  async ({ verificationData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/rent/verify`, verificationData, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Payment verification failed" });
    }
  }
);

// Get rent payment history
export const GetRentHistory = createAsyncThunk(
  "rentPayment/getHistory",
  async ({ tenancyId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/rent/history/${tenancyId}`, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch payment history" });
    }
  }
);

// Get overdue rents for landlord
export const GetOverdueRents = createAsyncThunk(
  "rentPayment/getOverdue",
  async ({ landlordId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/rent/overdue/${landlordId}`, { headers: authHeaders() });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch overdue rents" });
    }
  }
);

// Send rent reminder
export const SendRentReminder = createAsyncThunk(
  "rentPayment/sendReminder",
  async ({ tenantId, tenancyId, message }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/rent/send-reminder`,
        { tenantId, tenancyId, message },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to send reminder" });
    }
  }
);

// Get landlord rent collection summary
export const GetLandlordRentSummary = createAsyncThunk(
  "rentPayment/getLandlordSummary",
  async ({ landlordId, month, year }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/api/rent/landlord-summary/${landlordId}`,
        { params: { month, year }, headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to fetch summary" });
    }
  }
);

const RentPaymentSlice = createSlice({
  name: "rentPayment",
  initialState: {
    loading: false,
    pendingRents: [],
    rentHistory: [],
    currentRentDue: null,
    overdueRents: [],
    landlordSummary: null,
    checkoutUrl: null,
    error: "",
    success: false,
  },
  reducers: {
    resetRentPaymentState: (state) => {
      state.loading = false;
      state.error = "";
      state.success = false;
      state.currentRentDue = null;
      state.checkoutUrl = null;
    },
    clearRentPaymentError: (state) => {
      state.error = "";
    },
  },
  extraReducers: (builder) => {
    // Get Pending Rent
    builder.addCase(GetPendingRent.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetPendingRent.fulfilled, (state, action) => {
      state.loading = false;
      state.pendingRents = action.payload?.data || [];
    });
    builder.addCase(GetPendingRent.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch pending rent";
    });

    // Get Rent Due
    builder.addCase(GetRentDue.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetRentDue.fulfilled, (state, action) => {
      state.loading = false;
      state.currentRentDue = action.payload?.data;
    });
    builder.addCase(GetRentDue.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch rent due";
    });

    // Initiate Rent Payment
    builder.addCase(InitiateRentPayment.pending, (state) => {
      state.loading = true;
      state.error = "";
      state.success = false;
    });
    builder.addCase(InitiateRentPayment.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      state.checkoutUrl = action.payload?.data?.checkoutUrl;
    });
    builder.addCase(InitiateRentPayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to initiate payment";
    });

    // Verify Rent Payment
    builder.addCase(VerifyRentPayment.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(VerifyRentPayment.fulfilled, (state, action) => {
      state.loading = false;
      state.success = true;
      // Update pending rents - remove paid one
      const paidId = action.payload?.data?.rentPaymentId;
      state.pendingRents = state.pendingRents.filter(r => r._id !== paidId);
      state.currentRentDue = null;
    });
    builder.addCase(VerifyRentPayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Payment verification failed";
    });

    // Get Rent History
    builder.addCase(GetRentHistory.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetRentHistory.fulfilled, (state, action) => {
      state.loading = false;
      state.rentHistory = action.payload?.data || [];
    });
    builder.addCase(GetRentHistory.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch payment history";
    });

    // Get Overdue Rents
    builder.addCase(GetOverdueRents.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetOverdueRents.fulfilled, (state, action) => {
      state.loading = false;
      state.overdueRents = action.payload?.data || [];
    });
    builder.addCase(GetOverdueRents.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch overdue rents";
    });

    // Send Rent Reminder
    builder.addCase(SendRentReminder.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(SendRentReminder.fulfilled, (state) => {
      state.loading = false;
      state.success = true;
    });
    builder.addCase(SendRentReminder.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to send reminder";
    });

    // Get Landlord Summary
    builder.addCase(GetLandlordRentSummary.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(GetLandlordRentSummary.fulfilled, (state, action) => {
      state.loading = false;
      state.landlordSummary = action.payload?.data;
    });
    builder.addCase(GetLandlordRentSummary.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload?.message || "Failed to fetch summary";
    });
  },
});

export const { resetRentPaymentState, clearRentPaymentError } = RentPaymentSlice.actions;
export default RentPaymentSlice.reducer;
